import React, { useCallback, useContext, useEffect, useState } from "react";

import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Clipboard } from "react-feather";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { AdminGetGroupsByOrgResponse } from "@safetyculture/s12-apis-connect-web/s12/groups/admin/v1/admin_groups_pb";
import {
  GroupType as BaseGroupType,
  Filter,
  PaginationOption,
} from "@safetyculture/s12-apis-connect-web/s12/groups/v1/common_pb";
import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  UserSortType,
  UserStatus,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import { NO_DISPLAY_DATA } from "../../../constants";
import { subscriptionSeatTypesMapping } from "../../../constants/seats";
import useDebouncedEffect from "../../../helpers/hooks/use-debounced-effect";
import { convertUUIDtoS12Id, toUUID } from "../../../helpers/s12-helpers";
import mainStyles from "../../../styles";
import { DocumentSearch } from "../../search/constants";
import { BulkCreateDynamicGroupsModal } from "./bulk-create-dynamic-groups-modal/bulk-create-dynamic-groups-modal";
import {
  getOrgUserList,
  getOrganizationGroups,
} from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import RPCTablePaginationActions from "components/shared/table/rpc-table-pagination-actions";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

const USERS_PER_PAGE = 10;
const GROUPS_PER_PAGE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];

type GroupType = BaseGroupType | "Dynamic";

const GROUP_TYPE_MAP: Partial<Record<GroupType, string>> = {
  [BaseGroupType.UNSPECIFIED]: "Unspecified",
  [BaseGroupType.STATIC]: "Static",
  [BaseGroupType.SITE]: "Site",
  [BaseGroupType.COMPANY]: "Company",
  Dynamic: "Rule-based",
};

const userTableHeadings: Array<{
  title: string;
  key: string;
  width: string;
  align?: "left" | "center" | "right" | "justify" | "inherit";
  paddingRight?: number;
}> = [
  { title: "Name", key: "user-heading-name", width: "20%" },
  { title: "Email", key: "user-heading-email", width: "30%" },
  { title: "Seat Type", key: "user-heading-seat-type", width: "15%" },
  {
    title: "User ID",
    key: "user-heading-user-id",
    width: "35%",
    align: "right",
    paddingRight: 72,
  },
];

const groupTableHeadings: Array<{
  title: string;
  key: string;
  width: string;
  align?: "left" | "center" | "right" | "justify" | "inherit";
  paddingRight?: number;
}> = [
  { title: "Name", key: "group-heading-name", width: "30%" },
  { title: "Type", key: "group-heading-type", width: "20%" },
  {
    title: "Group ID",
    key: "group-heading-id",
    width: "50%",
    align: "right",
    paddingRight: 82,
  },
];

const displaySelectGroupTypeItems = () => {
  const groupTypeSelectOptions: Array<{
    label: string;
    value: GroupType | "ALL";
    hidden?: boolean;
  }> = [
    { label: "All", value: "ALL" },
    { label: "Static", value: BaseGroupType.STATIC },
    { label: "Rule-based", value: "Dynamic" },
    { label: "Company", value: BaseGroupType.COMPANY, hidden: true },
    { label: "Site", value: BaseGroupType.SITE, hidden: true },
  ];

  return groupTypeSelectOptions.map((opt) => {
    const { label, value, hidden } = opt;
    if (hidden) {
      return null;
    }

    return (
      <MenuItem
        key={`group-type-option-${value.toString().toLowerCase()}`}
        value={value}
        data-anchor={`group-type-option-${value.toString().toLowerCase()}`}
        data-testid={`group-type-option-${value.toString().toLowerCase()}`}
      >
        {label}
      </MenuItem>
    );
  });
};

const getGroupTypeString = (type: GroupType): string => {
  return GROUP_TYPE_MAP[type] || "Unspecified";
};

const getSeatTypeString = (
  seatType: SubscriptionSeatTypes | undefined,
): string => {
  return seatType !== undefined && seatType in subscriptionSeatTypesMapping
    ? subscriptionSeatTypesMapping[seatType]
    : NO_DISPLAY_DATA;
};

interface GroupUserTableProps {
  users: string[];
  regionCode: string;
  roleId: string;
  handleCopy: (
    textToCopy: string,
  ) => (e: React.MouseEvent<unknown, MouseEvent>) => void;
}

const useUserData = (
  users: string[],
  regionCode: string,
  roleId: string,
  pageSize: number,
) => {
  const [usersData, setUsersData] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const { showError } = useContext(SnackbarContext);

  const fetchUsers = useCallback(
    async (userIds: string[], currentPage: number, itemsPerPage: number) => {
      if (userIds.length === 0) return;

      setLoading(true);
      try {
        const offset = currentPage * itemsPerPage;
        setTotalItems(userIds.length);

        const convertedIds = userIds.map((id) =>
          convertUUIDtoS12Id(id, DocumentSearch.User),
        );

        const res = await getOrgUserList(
          {
            organizationId: roleId,
            pageSize: itemsPerPage,
            offset: offset,
            sortDirection: SortDirection.ASC,
            sortType: UserSortType.FIRST_NAME,
            userIds: convertedIds,
            userStatus: UserStatus.UNSPECIFIED,
          },
          callOptionsHeaders(regionCode, roleId),
        );

        setUsersData(res.users);
      } catch (e) {
        console.error("Error fetching users:", e);
        showError(e as GrpcError);
      } finally {
        setLoading(false);
      }
    },
    [regionCode, roleId, showError],
  );

  useEffect(() => {
    setUsersData([]);
    setPage(0);
    setTotalItems(0);
    if (users.length > 0) {
      fetchUsers(users, 0, pageSize);
    }
  }, [users, fetchUsers, pageSize]);

  return {
    usersData,
    loading,
    page,
    totalItems,
    setPage,
    fetchUsers,
  };
};

const GroupUserTable = ({
  regionCode,
  roleId,
  users,
  handleCopy,
}: GroupUserTableProps) => {
  const [pageSize, setPageSize] = useState(USERS_PER_PAGE);
  const { usersData, loading, page, totalItems, setPage, fetchUsers } =
    useUserData(users, regionCode, roleId, pageSize);

  if (users.length === 0) {
    return (
      <Typography style={{ textAlign: "center" }}>
        This group has no users.
      </Typography>
    );
  }

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    fetchUsers(users, newPage, pageSize);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(0);
    fetchUsers(users, 0, newPageSize);
  };

  return (
    <Box>
      <Table>
        <TableHead>
          <TableRow>
            {userTableHeadings.map((heading) => (
              <TableCell
                key={heading.key}
                style={{
                  width: heading.width,
                  textAlign: heading.align,
                  paddingRight: heading.paddingRight,
                }}
              >
                <Typography variant="subtitle2" color="textSecondary">
                  <b>{heading.title}</b>
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {usersData.map((user) => (
            <TableRow key={user.id}>
              {userTableHeadings.map((heading) => (
                <TableCell
                  key={`${heading.key}-${user.id}`}
                  style={{
                    width: heading.width,
                    textAlign: heading.align,
                    paddingRight: heading.paddingRight,
                  }}
                >
                  {heading.key === "user-heading-name" && (
                    <Button
                      href={`/user/${regionCode}/${user.id}`}
                      color="primary"
                    >
                      {user.firstName} {user.lastName}
                    </Button>
                  )}
                  {heading.key === "user-heading-email" && user.email}
                  {heading.key === "user-heading-seat-type" &&
                    getSeatTypeString(user.seatType)}
                  {heading.key === "user-heading-user-id" && (
                    <Box
                      component="span"
                      onClick={handleCopy(user.id)}
                      sx={{
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        float: "right",
                      }}
                    >
                      <Typography sx={{ color: "text.secondary" }}>
                        {user.id}
                      </Typography>
                      <Clipboard
                        color="inherit"
                        size={"1rem"}
                        style={{ marginLeft: 4 }}
                      />
                    </Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {loading && <LinearProgress style={{ marginTop: 16 }} />}
      <TablePagination
        component="div"
        count={totalItems}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        ActionsComponent={RPCTablePaginationActions}
      />
    </Box>
  );
};

interface OrgGroupsTabProps {
  regionCode: string;
  roleId: string;
}
const OrgGroupsTab = ({ regionCode, roleId }: OrgGroupsTabProps) => {
  const classes = mainStyles();
  const theme = useTheme();

  const [groups, setGroups] = useState<AdminGetGroupsByOrgResponse["groups"]>(
    [],
  );
  const [dynamicGroupModalOpen, setDynamicGroupModalOpen] = useState(false);
  const [localGroupsQuery, setLocalGroupsQuery] = useState<string>("");
  const [groupsQuery, setGroupsQuery] = useState<string>("");
  useDebouncedEffect(
    () => {
      setGroupsQuery(localGroupsQuery);
    },
    250,
    [localGroupsQuery],
  );
  const [groupMembers, setGroupMembers] = useState<Record<string, string[]>>(
    {},
  );
  const [expandedGroup, setExpandedGroup] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(GROUPS_PER_PAGE);
  const [nextPageToken, setNextPageToken] = useState("");
  const [pageTokens, setPageTokens] = useState<string[]>([""]);
  const [totalItems, setTotalItems] = useState(0);
  const [isEmptySubsequentPage, setIsEmptySubsequentPage] = useState(false);
  const [selectedGroupType, setSelectedGroupType] = useState<GroupType | "ALL">(
    "ALL",
  );

  const { copyToClipboard, showError } = useContext(SnackbarContext);

  const fetchGroups = useCallback(
    async (page: number, itemsPerPage: number, token?: string) => {
      try {
        setLoading(true);
        let groupType = selectedGroupType;
        // isDynamic filter should be undefined by default
        // unless particular group type is selected
        let isDynamic: undefined | boolean;
        switch (selectedGroupType) {
          case "ALL":
            groupType = BaseGroupType.UNSPECIFIED;
            break;
          case "Dynamic":
            groupType = BaseGroupType.STATIC;
            isDynamic = true;
            break;
          case BaseGroupType.STATIC:
            groupType = selectedGroupType;
            isDynamic = false;
            break;
          default:
            groupType = selectedGroupType;
            break;
        }
        const res = await getOrganizationGroups(
          {
            organizationId: toUUID(roleId),
            pageToken: token,
            includeMembers: true,
            groupType: groupType,
            pagingOption: new PaginationOption({
              pageSize: itemsPerPage,
            }),
            filter: new Filter({
              isDynamic,
              query: groupsQuery,
            }),
          },
          callOptionsHeaders(regionCode, roleId),
        );

        setIsEmptySubsequentPage(page > 0 && res.groups.length === 0);

        setGroups(res.groups);
        setNextPageToken(res.nextPageToken || "");

        const newMembers: Record<string, string[]> = {};
        for (const [key, value] of Object.entries(res.groupsMembers)) {
          const userType = value.userTypes[1];
          if (userType) {
            newMembers[key] = userType.userIds;
          }
        }
        setGroupMembers(newMembers);

        setTotalItems(
          page * itemsPerPage +
            res.groups.length +
            (res.nextPageToken ? itemsPerPage : 0),
        );
      } catch (e) {
        console.error("Error fetching groups:", e);
        showError(e as GrpcError);
      } finally {
        setLoading(false);
      }
    },
    [regionCode, roleId, showError, selectedGroupType, groupsQuery],
  );

  useEffect(() => {
    setNextPageToken("");
    setCurrentPage(0);
    setPageTokens([""]);
    setIsEmptySubsequentPage(false);
    fetchGroups(0, pageSize);
  }, [fetchGroups, pageSize, selectedGroupType]);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    if (newPage > currentPage) {
      if (!nextPageToken) return;
      setPageTokens((prev) => [...prev, nextPageToken]);
      setCurrentPage(newPage);
      fetchGroups(newPage, pageSize, nextPageToken);
    } else {
      setCurrentPage(newPage);
      fetchGroups(newPage, pageSize, pageTokens[newPage]);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(0);
    setNextPageToken("");
    setPageTokens([""]);
    fetchGroups(0, newPageSize);
  };

  const handleChange =
    (groupId: string) =>
    (event: React.ChangeEvent<unknown>, isExpanded: boolean) => {
      setExpandedGroup(isExpanded ? groupId : "");
    };

  const handleCopy =
    (textToCopy: string) => (e: React.MouseEvent<unknown, MouseEvent>) => {
      e.stopPropagation();
      copyToClipboard(textToCopy);
    };

  return (
    <Box p={2}>
      <BulkCreateDynamicGroupsModal
        open={dynamicGroupModalOpen}
        setOpen={setDynamicGroupModalOpen}
        orgID={toUUID(roleId)}
      />
      <Grid
        p={2}
        flexDirection="row"
        container
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item alignSelf="center">
          <FormControl variant="outlined" size="small" sx={classes.formControl}>
            <TextField
              id="groups-search"
              label="Search Groups"
              value={localGroupsQuery}
              onChange={(e) => setLocalGroupsQuery(e.target.value)}
              variant="outlined"
              size="small"
              style={{ width: "100%" }}
              inputProps={{ "data-testid": "groups-search" }}
              onKeyUp={(event) => {
                if (event.key === "Enter") {
                  return;
                }
              }}
            />
          </FormControl>
        </Grid>
        <Grid item spacing={2} justifyContent="flex-end" alignItems="center">
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <FormControl size="small" style={{ width: 200 }}>
                <InputLabel id="group-type-filter-label">Group Type</InputLabel>
                <Select
                  labelId="group-type-filter-label"
                  value={selectedGroupType}
                  label="Group Type"
                  onChange={(event) => {
                    setSelectedGroupType(
                      event.target.value as GroupType | "ALL",
                    );
                    setCurrentPage(0);
                  }}
                >
                  {displaySelectGroupTypeItems()}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Button
                onClick={() => setDynamicGroupModalOpen(true)}
                variant="contained"
                color="secondary"
                data-testid="create-dynamic-groups-csv-button"
              >
                Upload Rule-based Groups CSV
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {loading && groups.length === 0 ? (
        <LinearProgress />
      ) : groups.length === 0 ? (
        <Typography style={{ padding: "50px", textAlign: "center" }}>
          {isEmptySubsequentPage
            ? "There are no more groups"
            : selectedGroupType === "ALL"
              ? "This organisation has no groups"
              : `This organisation has no ${getGroupTypeString(
                  selectedGroupType as GroupType,
                ).toLowerCase()} groups`}
        </Typography>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              {groupTableHeadings.map((heading) => (
                <TableCell
                  key={heading.key}
                  style={{
                    width: heading.width,
                    textAlign: heading.align,
                    paddingRight: heading.paddingRight,
                  }}
                >
                  <Typography variant="subtitle2" color="textSecondary">
                    <b>{heading.title}</b>
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.id}>
                <TableCell colSpan={3} style={{ padding: 0 }}>
                  <Accordion
                    expanded={expandedGroup === group.id}
                    onChange={handleChange(group.id)}
                    TransitionProps={{ unmountOnExit: true }}
                    sx={{ mb: 1 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box display="flex" alignItems="center" width="100%">
                        <TableCell style={{ width: "30%", border: "none" }}>
                          <Box display="flex" alignItems="center">
                            <Typography style={{ marginRight: 8 }}>
                              {group.name}
                            </Typography>
                            <Typography variant="caption" color="primary">
                              ({groupMembers[group.id]?.length || 0} users)
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell style={{ width: "20%", border: "none" }}>
                          <Typography
                            style={{ color: theme.palette.text.secondary }}
                          >
                            {getGroupTypeString(
                              group.dynamic ? "Dynamic" : group.groupType,
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell
                          style={{
                            width: "50%",
                            textAlign: "right",
                            paddingRight: 42,
                            border: "none",
                          }}
                        >
                          <Box
                            component="span"
                            onClick={handleCopy(group.id)}
                            sx={classes.clickable}
                            display="inline-flex"
                            alignItems="center"
                          >
                            <Typography
                              style={{ color: theme.palette.text.secondary }}
                            >
                              {group.id}
                            </Typography>
                            <Clipboard
                              color={theme.palette.text.secondary}
                              size={"1rem"}
                              style={{ marginLeft: 4 }}
                            />
                          </Box>
                        </TableCell>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <GroupUserTable
                        regionCode={regionCode}
                        roleId={roleId}
                        users={groupMembers[group.id] || []}
                        handleCopy={handleCopy}
                      />
                    </AccordionDetails>
                  </Accordion>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {loading && groups.length > 0 && (
        <LinearProgress style={{ marginTop: 16 }} />
      )}

      {(groups.length > 0 || isEmptySubsequentPage) && (
        <TablePagination
          component="div"
          count={totalItems}
          page={currentPage}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          ActionsComponent={RPCTablePaginationActions}
          labelDisplayedRows={({ from, to }) =>
            `${from}–${to}${
              nextPageToken ? ` of more than ${to}` : ` of ${totalItems}`
            }`
          }
        />
      )}
    </Box>
  );
};
export default OrgGroupsTab;
